import React, { useEffect, useMemo, Suspense, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useActiveEnvironment } from '../../../hooks/useEnvironments'
import { useCollection } from '../../../resources/hooks/useCollection'
import { ErrorBoundaryErrorPage } from '../../../components/Shared/CustomErrorComponent'
import { SpaceRedirect } from './SpaceRedirect'
import { AutobuildLoadingScreen } from './AutobuildLoadingScreen'
import { Button, Text, Icon, Alert } from '@flatfile/baseline'
import getCollection from '../../../resources/support/getCollection'
import { logger } from '../../../utils/logging'
import { useNavigate } from 'react-router-dom'

export type BlueprintView = 'workbook' | 'textEditor' | 'summary'

export const SpaceStep: React.FC<{
  appId: string
}> = ({ appId }) => {
  const { environment, setActiveEnvironment } = useActiveEnvironment()
  const [isLoading, setIsLoading] = useState(false)
  const [targetEnvironment, setTargetEnvironment] = useState<{ id: string; name: string } | null>(null)
  const navigate = useNavigate()

  // Get all spaces for this app across all environments with a single API call
  const allSpaces = useCollection('SPACES', {
    app: appId,
    filterByEnvId: false,
  })

  // Find template in current environment by filtering client-side
  const templateInCurrentEnv = useMemo(
    //metadata approach is deprecated in favor of isAppTemplate, but we need to have some backwards compatibility
    () => allSpaces.find((s) => s.isAppTemplate && s.environmentId === environment.id),
    [allSpaces, environment.id]
  )

  // Find template in any environment except the current one
  const templateInAnyEnv = useMemo(() => {
    if (templateInCurrentEnv) return null // No need to look further if we have one in current env

    //metadata approach is deprecated in favor of isAppTemplate, but we need to have some backwards compatibility
    const space = allSpaces.find((s) => s.isAppTemplate && s.environmentId !== environment.id)
    if (!space) return null

    // If environmentId is missing, we can't determine the environment
    if (!space.environmentId) return null

    // Get environments for the name (we still need the name for the UI)
    const environments = getCollection('ENVIRONMENTS').getItems()
    const spaceEnv = environments.find((env) => env.id === space.environmentId)

    if (spaceEnv) {
      return { space, environment: spaceEnv }
    }

    // If we can't find environment details, just use the ID
    return {
      space,
      environment: {
        id: space.environmentId,
        name: space.environmentId,
      },
    }
  }, [allSpaces, templateInCurrentEnv, environment.id])

  // Find an environment to use
  useEffect(() => {
    if (!templateInCurrentEnv && templateInAnyEnv && !targetEnvironment) {
      setTargetEnvironment({
        id: templateInAnyEnv.environment.id,
        name: templateInAnyEnv.environment.name,
      })
    }
  }, [templateInCurrentEnv, templateInAnyEnv, targetEnvironment])

  const handleSwitchEnvironment = () => {
    if (targetEnvironment) {
      setIsLoading(true)
      try {
        setActiveEnvironment(targetEnvironment.id)
        // The component will re-render with the new environment
      } catch (error) {
        logger.error('Failed to switch environment', { error })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleBackToDashboard = () => {
    // Navigate to home to close the AutoBuildFlow modal
    navigate('/home')
  }

  if (isLoading) {
    return <AutobuildLoadingScreen stage='loading-space' />
  }

  if (templateInCurrentEnv) {
    return (
      <ErrorBoundary
        fallbackRender={({ error }) => (
          <ErrorBoundaryErrorPage
            error={error}
            page='Autobuildflow.tsx'
            message='There was an error loading your autobuild space.'
          />
        )}
      >
        <Suspense fallback={<AutobuildLoadingScreen stage='loading-space' />}>
          <div className='h-full flex-1 w-full'>
            <SpaceRedirect spaceId={templateInCurrentEnv.id} />
          </div>
        </Suspense>
      </ErrorBoundary>
    )
  }

  // Show environment switch screen
  if (templateInAnyEnv && targetEnvironment) {
    return (
      <div className='flex flex-col h-full items-center justify-center'>
        <div className='flex flex-col max-w-lg items-center justify-center'>
          <Alert variant='outline'>
            <div className='flex flex-row items-center justify-center gap-2'>
              <Icon name='status-warning-circle' color='warning size-6' />{' '}
              <Text className='text-xl font-medium'>Switch Environments?</Text>
            </div>
            <Text className='mt-4' as='div'>
              We found your Autobuild app in the{' '}
              <Text variant='body' emphasized>
                {targetEnvironment.name}
              </Text>{' '}
              environment, but you're currently in{' '}
              <Text variant='body' emphasized>
                {environment.name}
              </Text>
              .
            </Text>
            <Text as='div' className='mt-2'>
              Would you like to switch to {targetEnvironment.name} to continue?
            </Text>
            <div className='flex gap-2 justify-end items-end mt-8'>
              <Button variant='outline' size='sm' onClick={handleBackToDashboard}>
                Take me back to the Dashboard
              </Button>
              <Button variant='primary' size='sm' onClick={handleSwitchEnvironment} loading={isLoading}>
                Switch Environment
              </Button>
            </div>
          </Alert>
        </div>
      </div>
    )
  }

  // Show error screen if no template found in any environment
  return (
    <div className='flex flex-col h-full'>
      <div className='flex-1 flex flex-col items-center justify-center w-full'>
        <div className='flex flex-col items-center max-w-md border-border border-solid rounded-lg'>
          <div className='flex flex-row items-center gap-2'>
            <Icon name='status-warning-triangle' className='text-destructive size-6' />{' '}
            <Text className='text-xl font-medium'>No Template Found</Text>
          </div>
          <Text className='mt-4'>We couldn't find a template for your autobuild app in any environment.</Text>
          <Text className='mt-4'>Please start a new autobuild app.</Text>
          <div className='flex justify-center mt-8'>
            <Button variant='primary' size='sm' onClick={handleBackToDashboard} className='mx-2'>
              Go back to the dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
